import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { authApi } from '@/api/auth'
import { Alert, AuthLayout, Button, Field } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { useAuthStore } from '@/stores/authStore'

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
})

type FormValues = z.infer<typeof schema>

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)
  const translateError = useApiError()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const mutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (session) => {
      setSession(session)
      navigate('/', { replace: true })
    },
  })

  return (
    <AuthLayout
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle')}
      footer={
        <>
          {t('auth.login.noAccount')}{' '}
          <Link to="/register" className="font-semibold text-brand-700 underline">
            {t('auth.login.registerLink')}
          </Link>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError && <Alert>{translateError(mutation.error)}</Alert>}

        <Field
          label={t('auth.fields.email')}
          type="email"
          autoComplete="email"
          inputMode="email"
          autoFocus
          error={errors.email && t('auth.validation.email')}
          {...register('email')}
        />

        <Field
          label={t('auth.fields.password')}
          type="password"
          autoComplete="current-password"
          error={errors.password && t('auth.validation.required')}
          {...register('password')}
        />

        <Button type="submit" loading={mutation.isPending}>
          {t('auth.login.submit')}
        </Button>

        <Link
          to="/magic-link"
          className="text-center text-sm font-medium text-brand-700 underline"
        >
          {t('auth.login.magicLink')}
        </Link>
      </form>
    </AuthLayout>
  )
}
